import { useCallback, useEffect, useState } from 'react'
import { fetchRoomSnapshot, subscribeToRoom, leaveRoom, type RoomSnapshot } from '../lib/api/rooms'
import { supabase } from '../lib/supabase'
import { JoinLobbyPhase } from './phases/JoinLobbyPhase'
import { JoinBuildingPhase } from './phases/JoinBuildingPhase'
import { JoinPresentingPhase } from './phases/JoinPresentingPhase'
import { JoinResultsPhase } from './phases/JoinResultsPhase'
import { useT } from '../i18n'

interface Props {
  roomId: string
  roomCode: string
  onLeave: () => void
}

/**
 * Phone guest view of a TV room. Orchestrator: pulls a full snapshot on
 * every realtime tick, finds the caller's own player row, and routes to
 * the right phase view. Gameplay (writing / voting) lives in the phase
 * components.
 */
export function JoinRoomScreen({ roomId, roomCode, onLeave }: Props) {
  const t = useT()
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null)
  const [myId, setMyId] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    const snap = await fetchRoomSnapshot(roomId)
    if (snap) setSnapshot(snap)
  }, [roomId])

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setMyId(data.user?.id ?? null))
  }, [])

  useEffect(() => {
    refetch()
    const off = subscribeToRoom(roomId, refetch)
    return () => { off() }
  }, [roomId, refetch])

  async function handleLeave() {
    try { await leaveRoom(roomId) } catch { /* best-effort */ }
    onLeave()
  }

  const room = snapshot?.room
  const players = snapshot?.players ?? []
  const me = players.find(p => p.profile_id === myId) ?? null

  return (
    <div className="min-h-screen flex flex-col bg-brand-bg">
      <header className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted">
            Room
          </p>
          <p className="font-display text-2xl font-bold tracking-[0.15em]">{roomCode}</p>
        </div>
        <button onClick={handleLeave} className="text-sm text-brand-muted underline">
          Leave
        </button>
      </header>

      <div className="flex-1 flex flex-col overflow-y-auto">
        {!room || !snapshot ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-brand-muted">Loading…</p>
          </div>
        ) : !me && room.status !== 'results' ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
            <p className="font-display text-2xl font-bold mb-2">{t('You left this room')}</p>
            <p className="text-brand-muted mb-6">{t('Rejoin from the home screen if you want back in.')}</p>
            <button onClick={onLeave} className="btn-outline">{t('Back to home')}</button>
          </div>
        ) : room.status === 'lobby' ? (
          <JoinLobbyPhase
            playerName={me?.display_name ?? 'you'}
            players={players.map(p => p.display_name)}
          />
        ) : (room.status === 'building' || room.status === 'dealing') ? (
          me ? <JoinBuildingPhase snapshot={snapshot} me={me} /> : null
        ) : (room.status === 'presenting' || room.status === 'voting') ? (
          me ? <JoinPresentingPhase snapshot={snapshot} me={me} /> : null
        ) : room.status === 'results' ? (
          <JoinResultsPhase snapshot={snapshot} myId={myId} />
        ) : (
          <div className="flex-1 flex items-center justify-center text-center">
            <p className="text-brand-muted">Room is in an unknown state ({room.status}).</p>
          </div>
        )}
      </div>
    </div>
  )
}
